import chroma from 'chroma-js';
import { generateContrasts, generatePalette } from './color-palette-generator';

export interface CssStyleDeclaration {
  var: string;
  value: string;
}

export type MaterialColorScheme = 'light' | 'dark' | 'color-scheme';

interface BrandTheme {
  primary?: string;
  accent?: string;
  warn?: string;
  controls?: string;
}

interface ColorRole {
  base: string;
  palette: Record<number, string>;
}

export function generateStylesByColorHex(
  hex: string,
  name: string
): CssStyleDeclaration[] {
  const palette = generatePalette(hex) as Record<number, string>;
  const contrasts = generateContrasts(palette);
  const styles: CssStyleDeclaration[] = [];
  styles.push({
    var: `--fuse-${name}`,
    value: hex,
  });
  styles.push({
    var: `--fuse-on-${name}`,
    value: contrasts[500],
  });
  styles.push({
    var: `--fuse-${name}-rgb`,
    value: chroma(hex).rgb().join(','),
  });
  styles.push({
    var: `--fuse-on-${name}-rgb`,
    value: chroma(contrasts[500]).rgb().join(','),
  });
  Object.keys(palette).forEach((variant) => {
    styles.push({
      var: `--fuse-${name}-${variant}`,
      value: palette[Number(variant)],
    });
    styles.push({
      var: `--fuse-${name}-${variant}-rgb`,
      value: chroma(palette[Number(variant)]).rgb().join(','),
    });
  });

  Object.keys(contrasts).forEach((variant) => {
    styles.push({
      var: `--fuse-on-${name}-${variant}`,
      value: contrasts[Number(variant)],
    });
    styles.push({
      var: `--fuse-on-${name}-${variant}-rgb`,
      value: chroma(contrasts[Number(variant)]).rgb().join(','),
    });
  });
  return styles;
}

export function generateMaterialSystemVariables(
  theme: BrandTheme,
  scheme: MaterialColorScheme = 'color-scheme'
): CssStyleDeclaration[] {
  if (!theme?.primary) {
    return [];
  }

  const values =
    scheme === 'color-scheme'
      ? createColorSchemeValues(theme)
      : buildMaterialSystemValues(theme, scheme === 'dark');

  return Object.entries({
    ...values,
    ...getTypographySystemValues(),
  }).map(([cssVariable, value]) => ({
    var: cssVariable,
    value,
  }));
}

function createColorSchemeValues(theme: BrandTheme): Record<string, string> {
  const lightValues = buildMaterialSystemValues(theme, false);
  const darkValues = buildMaterialSystemValues(theme, true);

  return Object.fromEntries(
    Object.entries(lightValues).map(([cssVariable, lightValue]) => {
      const darkValue = darkValues[cssVariable];

      return [
        cssVariable,
        lightValue === darkValue
          ? lightValue
          : `light-dark(${lightValue}, ${darkValue})`,
      ];
    })
  );
}

function buildMaterialSystemValues(
  theme: BrandTheme,
  isDark: boolean
): Record<string, string> {
  const primaryColor = theme.primary as string;
  const tertiary = buildColorRole(theme.accent || primaryColor);
  const error = buildColorRole(theme.warn || '#dc2626');
  const controls = buildColorRole(theme.controls || primaryColor);
  const surface = getSurfaceRoles(isDark);

  return {
    '--mat-sys-primary': controls.base,
    '--mat-sys-on-primary': getReadableColor(controls.base),
    '--mat-sys-primary-container': tone(controls, isDark ? 800 : 100),
    '--mat-sys-on-primary-container': tone(controls, isDark ? 100 : 900),
    '--mat-sys-primary-fixed': tone(controls, 100),
    '--mat-sys-primary-fixed-dim': tone(controls, 200),
    '--mat-sys-on-primary-fixed': tone(controls, 900),
    '--mat-sys-on-primary-fixed-variant': tone(controls, 700),
    '--mat-sys-inverse-primary': tone(controls, isDark ? 600 : 300),
    '--mat-sys-secondary': tertiary.base,
    '--mat-sys-on-secondary': getReadableColor(tertiary.base),
    '--mat-sys-secondary-container': tone(tertiary, isDark ? 800 : 100),
    '--mat-sys-on-secondary-container': tone(tertiary, isDark ? 100 : 900),
    '--mat-sys-secondary-fixed': tone(tertiary, 100),
    '--mat-sys-secondary-fixed-dim': tone(tertiary, 200),
    '--mat-sys-on-secondary-fixed': tone(tertiary, 900),
    '--mat-sys-on-secondary-fixed-variant': tone(tertiary, 700),
    '--mat-sys-tertiary': tertiary.base,
    '--mat-sys-on-tertiary': getReadableColor(tertiary.base),
    '--mat-sys-tertiary-container': tone(tertiary, isDark ? 800 : 100),
    '--mat-sys-on-tertiary-container': tone(tertiary, isDark ? 100 : 900),
    '--mat-sys-tertiary-fixed': tone(tertiary, 100),
    '--mat-sys-tertiary-fixed-dim': tone(tertiary, 200),
    '--mat-sys-on-tertiary-fixed': tone(tertiary, 900),
    '--mat-sys-on-tertiary-fixed-variant': tone(tertiary, 700),
    '--mat-sys-error': tone(error, isDark ? 300 : 600),
    '--mat-sys-on-error': tone(error, isDark ? 900 : 50),
    '--mat-sys-error-container': tone(error, isDark ? 800 : 100),
    '--mat-sys-on-error-container': tone(error, isDark ? 100 : 900),
    '--mat-sys-background': surface.background,
    '--mat-sys-on-background': surface.onSurface,
    '--mat-sys-surface': surface.surface,
    '--mat-sys-on-surface': surface.onSurface,
    '--mat-sys-surface-bright': surface.surfaceBright,
    '--mat-sys-surface-container': surface.surfaceContainer,
    '--mat-sys-surface-container-high': surface.surfaceContainerHigh,
    '--mat-sys-surface-container-highest': surface.surfaceContainerHighest,
    '--mat-sys-surface-container-low': surface.surfaceContainerLow,
    '--mat-sys-surface-container-lowest': surface.surfaceContainerLowest,
    '--mat-sys-surface-dim': surface.surfaceDim,
    '--mat-sys-surface-tint': controls.base,
    '--mat-sys-surface-variant': surface.surfaceVariant,
    '--mat-sys-on-surface-variant': surface.onSurfaceVariant,
    '--mat-sys-inverse-surface': surface.inverseSurface,
    '--mat-sys-inverse-on-surface': surface.inverseOnSurface,
    // '--mat-sys-outline': tone(controls, isDark ? 400 : 500),
    // '--mat-sys-outline-variant': tone(controls, isDark ? 700 : 200),
    '--mat-sys-shadow': '#000000',
    '--mat-sys-scrim': '#000000',
  };
}

function buildColorRole(hex: string): ColorRole {
  const palette = generatePalette(hex);

  return {
    base: normalizeColor(hex, palette[500]),
    palette,
  };
}

function tone(role: ColorRole, shade: number): string {
  return role.palette[shade] ?? role.base;
}

function normalizeColor(hex: string, fallback: string): string {
  return chroma.valid(hex) ? chroma(hex).hex() : fallback;
}

function getReadableColor(hex: string): string {
  return chroma.contrast(hex, '#ffffff') >= chroma.contrast(hex, '#000000')
    ? '#ffffff'
    : '#000000';
}

function getTypographySystemValues(): Record<string, string> {
  return {
    '--mat-sys-body-large': '400 0.875rem / 1.25rem var(--app-font)',
    '--mat-sys-body-large-font': 'var(--app-font)',
    '--mat-sys-body-large-line-height': '1.25rem',
    '--mat-sys-body-large-size': '0.875rem',
    '--mat-sys-body-large-tracking': '0.016rem',
    '--mat-sys-body-large-weight': '400',
  };
}

function getSurfaceRoles(isDark: boolean): Record<string, string> {
  if (isDark) {
    return {
      background: '#0f172a',
      inverseOnSurface: '#1e293b',
      inverseSurface: '#f1f5f9',
      onSurface: '#ffffff',
      onSurfaceVariant: '#cbd5e1',
      surface: '#0f172a',
      surfaceBright: '#334155',
      surfaceContainer: '#1e293b',
      surfaceContainerHigh: '#334155',
      surfaceContainerHighest: '#475569',
      surfaceContainerLow: '#172033',
      surfaceContainerLowest: '#020617',
      surfaceDim: '#0f172a',
      surfaceVariant: '#334155',
    };
  }

  return {
    background: '#f1f5f9',
    inverseOnSurface: '#f8fafc',
    inverseSurface: '#1e293b',
    onSurface: '#1e293b',
    onSurfaceVariant: '#475569',
    surface: '#ffffff',
    surfaceBright: '#ffffff',
    surfaceContainer: '#f8fafc',
    surfaceContainerHigh: '#FFFFFF',
    surfaceContainerHighest: '#e2e8f0',
    surfaceContainerLow: '#ffffff',
    surfaceContainerLowest: '#ffffff',
    surfaceDim: '#cbd5e1',
    surfaceVariant: '#e2e8f0',
  };
}
